import { useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { Box, Tooltip, ClickAwayListener } from '@mui/material';

import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';

import { DropdownList } from './components/dropdown-list';
import { useSelectRecord } from './hooks/use-select-record';
import { SearchInputField } from './components/search-input-field';
import { RecordSelectionDialog } from './components/record-selection-dialog';

// ----------------------------------------------------------------------

type RecordType = '' | 'Contact' | 'Channel' | 'Account' | 'User' | 'Team' | 'Conversation';

type Props = {
  name: string;
  label: string;
  disabled?: boolean;
  defaultValue?: string;
  isAccountCreate?: boolean;
  setSelectedUserAccDtl?: (data: any) => void;
  setValue?: any;
  type: RecordType;
  isOnlyWaChannel?: boolean;
  isAccountView?: boolean;
  currentContactId?: string | null;
  onAddBtnClick?: () => void;
  isAddButton?: boolean;
  placeholder?: string;
  required?: boolean;
  control?: any;
  onItemSelect?: (item: any) => void;
};

// ----------------------------------------------------------------------

export default function SearchSelectRecordField({
  name,
  label,
  disabled = false,
  defaultValue = '',
  isAccountCreate = false,
  setSelectedUserAccDtl = () => {},
  setValue = () => {},
  type,
  isOnlyWaChannel = false,
  isAccountView = false,
  currentContactId = null,
  onAddBtnClick = () => {},
  isAddButton = false,
  placeholder = '',
  required = false,
  control: propControl,
  onItemSelect: propOnItemSelect,
}: Props) {
  const anchorRef = useRef<HTMLDivElement>(null);

  const formContext = useFormContext();
  const control = propControl || formContext?.control;
  const errors = formContext?.formState?.errors || {};

  const {
    field: { value: fieldValue, onChange: fieldOnChange },
    fieldState: { error: fieldError, isTouched, isDirty },
  } = useController({
    name,
    control,
  });

  const isSubmitted = formContext?.formState?.isSubmitted;
  const error = (isTouched || isDirty || isSubmitted) ? (fieldError || errors[name]) : undefined;
  const helperText = error ? (error.message as string) : '';

  const displayLabel = required ? `${label} *` : label;

  const {
    openDialog,
    searchInput,
    dropdownData,
    selectedLabel,
    isDropdownLoading,
    tableData,
    isLoader,
    tableConfig,
    t,
    setOpenDialog,
    setSearchInput,
    setSelectedLabel,
    setDropdownData,
    handleItemSelect,
    handleRecordSelect,
  } = useSelectRecord({
    type,
    isAccountView,
    currentContactId,
    isOnlyWaChannel,
    defaultValue: fieldValue || defaultValue,
    name,
    setValue: (fieldName, value) => {
      fieldOnChange(value);
      setValue(fieldName, value);
    },
    setSelectedUserAccDtl,
    onItemSelect: propOnItemSelect,
  });

  const { isView: isContactView } = useModulePermissions('contact');
  const { isView: isCompanyView } = useModulePermissions('company');

  const isRestricted =
    (type === 'Contact' && !isContactView) || (type === 'Account' && !isCompanyView);

  const isDisabled = disabled || isRestricted;

  const handleInputChange = (value: string) => {
    if (isDisabled) return;
    setSelectedLabel(value);
    setSearchInput(value);
  };

  const handleClickAway = () => {
    setDropdownData([]);
    setSearchInput('');
  };

  const tooltipTitle =
    type === 'Contact'
      ? t('CONTACT_ACCESS_RESTRICTED') || 'Contact access is restricted. Please contact your administrator.'
      : t('ACCOUNT_ACCESS_RESTRICTED') || 'Account access is restricted. Please contact your administrator.';

  const content = (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ my: 1, width: '100%' }}>
          <SearchInputField
            label={displayLabel}
            value={selectedLabel}
            disabled={isDisabled}
            placeholder={placeholder}
            onChange={handleInputChange}
            onSearchClick={() => !isDisabled && setOpenDialog(true)}
            onAddClick={onAddBtnClick}
            isAddButton={isAddButton && !isDisabled}
            isAccountCreate={isAccountCreate}
            error={!!error}
            helperText={helperText}
            ref={anchorRef}
          />

          <DropdownList
            open={!isDisabled && (dropdownData.length > 0 || (!isDropdownLoading && !!searchInput))}
            anchorEl={anchorRef.current}
            data={dropdownData}
            isLoading={isDropdownLoading}
            type={type}
            onItemSelect={handleItemSelect}
          />
        </Box>
      </ClickAwayListener>

      <RecordSelectionDialog
        open={!isDisabled && openDialog}
        onClose={() => setOpenDialog(false)}
        type={type}
        tableData={tableData}
        tableConfig={tableConfig}
        isLoader={isLoader}
        onRecordSelect={handleRecordSelect}
        t={t}
      />
    </>
  );

  if (isRestricted) {
    return (
      <Tooltip title={tooltipTitle} arrow placement="top">
        <Box sx={{ width: '100%', cursor: 'not-allowed' }}>
          {content}
        </Box>
      </Tooltip>
    );
  }

  return content;
}
